import { type CSSProperties, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { PictureElement } from '@/pictureElement/Picture';
import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { useAnchorable } from '../../hooks/useAnchorable';

import { appendProtocol, isAnchorLink, isMailToLink, isTelLink } from '../../utils/link';

import { FigcaptionWrapper } from './FigcaptionWrapper';
import {
  image,
  imageAlignCenter,
  imageAlignLeft,
  imageAlignRight,
  imageBlockContainer,
  imagePaddingContainer,
} from './ImageBlock.css';
import type { TImageBlockProps } from './ImageBlock.types';

export {
  image as postImageClass,
  imageAlignCenter as postImageAlignCenterClass,
  imageAlignLeft as postImageAlignLeftClass,
  imageAlignRight as postImageAlignRightClass,
  imageBlockContainer as postImageBlockContainerClass,
  imagePaddingContainer as postImagePaddingContainerClass,
} from './ImageBlock.css';

const borderRadiusPx = (value: CSSProperties['borderRadius'] | undefined): string => {
  if (value == null || value === '') {
    return '0px';
  }

  const n = Number.parseFloat(String(value));
  return Number.isNaN(n) ? '0px' : `${n}px`;
};

const ImageBlock = ({
  element,
  children,
  className,
  rewriteImageSrc,
  isIndexingDisabled,
  translations,
  inEditor,
}: TImageBlockProps) => {
  const { attrs } = element;
  const {
    captionAlign,
    width,
    align,
    useIndividualBorderRadius,
    useIndividualBorderWidth,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    borderStyle,
    borderWidthTop,
    borderWidthRight,
    borderWidthBottom,
    borderWidthLeft,
    borderColor,
  } = attrs || {};

  const { utmParams } = usePostContentContext();

  // Caption related
  const captionURLWithUTMParams = evaluateAndAppendUTMParams(appendProtocol(attrs?.captionUrl), utmParams);
  const captionTarget = attrs?.captionTarget !== '_self' ? '_blank' : undefined;
  const captionAdditionalAttributes = {};
  const isCaptionClickTrackingDisabled =
    attrs?.clicktracking === 'off' ||
    isAnchorLink(attrs?.captionUrl || '') ||
    isMailToLink(attrs?.captionUrl || '') ||
    isTelLink(attrs?.captionUrl || '');
  if (isCaptionClickTrackingDisabled) {
    captionAdditionalAttributes['data-skip-tracking'] = 'true';
  }

  // Anchor related
  const [anchorUrlWithUTMParams, setAnchorUrlWithUTMParams] = useState(attrs?.url);
  // make sure to update the href *after* hydration to avoid mismatches
  useEffect(() => {
    setAnchorUrlWithUTMParams(evaluateAndAppendUTMParams(appendProtocol(attrs?.url), utmParams));
  }, [attrs?.url, utmParams]);
  const anchorTarget = attrs?.target !== '_self' ? '_blank' : undefined;
  const isAnchorClickTrackingDisabled =
    attrs?.clicktracking === 'off' ||
    isAnchorLink(attrs?.url || '') ||
    isMailToLink(attrs?.url || '') ||
    isTelLink(attrs?.url || '');
  const anchorAdditionalAttributes = {};
  if (isAnchorClickTrackingDisabled) {
    anchorAdditionalAttributes['data-skip-tracking'] = 'true';
  }

  const { anchorAttributes } = useAnchorable(element.attrs);

  const isChildFigCaption =
    Array.isArray(element.content) && element.content.length === 1 && element.content[0].type === 'figcaption';
  const shouldRenderFigCaption =
    inEditor ||
    (isChildFigCaption && Array.isArray(element?.content?.[0]?.content) && element?.content?.[0]?.content.length > 0);
  const hasLinkedFigCaption = Boolean(captionURLWithUTMParams) && (inEditor || isChildFigCaption);
  const shouldWrapFigCaptionWithLink = Boolean(captionURLWithUTMParams) && isChildFigCaption && !inEditor;

  const imageSrc = rewriteImageSrc && element?.attrs?.src ? rewriteImageSrc(element.attrs.src) : element?.attrs?.src;
  const imageAlignClassNames = {
    left: imageAlignLeft,
    right: imageAlignRight,
    center: imageAlignCenter,
  };

  const imageBorderRadius = useMemo(() => {
    if (useIndividualBorderRadius) {
      return [
        borderRadiusPx(borderTopLeftRadius),
        borderRadiusPx(borderTopRightRadius),
        borderRadiusPx(borderBottomRightRadius),
        borderRadiusPx(borderBottomLeftRadius),
      ].join(' ');
    }

    if (borderTopLeftRadius == null || borderTopLeftRadius === '') {
      return undefined;
    }

    return borderRadiusPx(borderTopLeftRadius);
  }, [
    useIndividualBorderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
  ]);

  const imageBorderWidthStyles = useMemo((): CSSProperties => {
    if (useIndividualBorderWidth) {
      return {
        boxSizing: 'border-box',
        borderWidth: [
          `${borderWidthTop}px`,
          `${borderWidthRight}px`,
          `${borderWidthBottom}px`,
          `${borderWidthLeft}px`,
        ].join(' '),
      };
    }

    const defaultWidth = borderWidthTop;

    if (!defaultWidth) {
      return {};
    }

    return {
      borderWidth: `${defaultWidth}px`,
      boxSizing: 'border-box',
    };
  }, [useIndividualBorderWidth, borderWidthTop, borderWidthRight, borderWidthBottom, borderWidthLeft]);

  const imageBorderStyle = useMemo(() => {
    if (Object.keys(imageBorderWidthStyles).length === 0) {
      return undefined;
    }

    return borderStyle || 'solid';
  }, [imageBorderWidthStyles, borderStyle]);

  const imageBorderColor = useMemo(() => {
    if (Object.keys(imageBorderWidthStyles).length === 0) {
      return undefined;
    }

    return borderColor || '#E5E7EB';
  }, [imageBorderWidthStyles, borderColor]);

  if (!imageSrc) {
    return (
      <div className="p-8 bg-red-100 border border-red-500 rounded">
        <p className="text-red-800">{translations?.['shared.image.missing_image']}</p>
      </div>
    );
  }
  const imgTag = (
    <PictureElement src={imageSrc}>
      <img
        src={imageSrc}
        alt=""
        style={{
          width,
          borderStyle: imageBorderStyle,
          borderRadius: imageBorderRadius,
          borderColor: imageBorderColor,
          ...imageBorderWidthStyles,
        }}
        className={classNames(image, align && imageAlignClassNames[align])}
      />
    </PictureElement>
  );

  return (
    <div
      {...anchorAttributes}
      className={classNames(className, imageBlockContainer)}
      style={
        // Only set the per-image override when explicitly defined so the CSS
        // variable can fall back to the theme-level `--wt-image-source-alignment`.
        (captionAlign
          ? {
              '--caption-alignment': captionAlign,
            }
          : {}) as CSSProperties
      }
    >
      <div className={classNames(imagePaddingContainer)}>
        {anchorUrlWithUTMParams ? (
          <a
            rel={isIndexingDisabled ? 'nofollow noopener noreferrer' : undefined}
            target={anchorTarget}
            href={anchorUrlWithUTMParams}
            className="text-decoration-none"
            {...anchorAdditionalAttributes}
          >
            {imgTag}
          </a>
        ) : (
          imgTag
        )}
        {shouldRenderFigCaption ? (
          shouldWrapFigCaptionWithLink ? (
            <a
              rel={isIndexingDisabled ? 'noopener' : undefined}
              target={captionTarget}
              href={captionURLWithUTMParams}
              className="underline underline-offset-2"
              {...captionAdditionalAttributes}
            >
              <FigcaptionWrapper linked>{children}</FigcaptionWrapper>
            </a>
          ) : (
            <FigcaptionWrapper linked={hasLinkedFigCaption}>{children}</FigcaptionWrapper>
          )
        ) : null}
      </div>
    </div>
  );
};

export { ImageBlock };
